import { Divider, Grid, Label, Loader } from "@aws-amplify/ui-react";
import { CCard } from "@coreui/react";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { API, graphqlOperation } from "aws-amplify";

import { getEquipment, getMaintenanceRoutine } from "graphql/queries";
import ActivitiesInfo from "./ActivitiesInfo";
import { OrderContext } from "components/serviceOrders/context/OrderContext";
import { useOnlineStatus } from "hooks/useOnlineStatus";
import { localGetServiceOrder } from "libs/idb/service-orders-db";

const MaintenanceRoutinesInfo = ({
  maintenanceRoutineIDs,
  equipmentRoutines,
}) => {
  const online = useOnlineStatus();
  const { serviceOrder } = useContext(OrderContext);
  const { onChangeActivity } = useContext(OrderContext);
  const [maintenanceRoutines, setMaintenanceRoutines] = useState({
    data: [],
    loading: false,
  });
  const [equipments, setEquipments] = useState({
    data: [],
    loading: false,
  });

  const getEquipments = async (ids) => {
    try {
      setEquipments({ data: [], loading: true });

      if (!online) {
        const localData = await localGetServiceOrder(serviceOrder.id);
        const equipmentsFound = localData?.equipments ?? [];
        setEquipments((prev) => ({ ...prev, data: equipmentsFound }));
        return;
      }

      const equipmentItems = [];
      if (ids.length > 0) {
        await Promise.all(
          ids.map(async (id) => {
            const equipmentData = await API.graphql(
              graphqlOperation(getEquipment, { id })
            );
            if (equipmentData.data.getEquipment) {
              equipmentItems.push(equipmentData.data.getEquipment);
            }
          })
        );
      }

      setEquipments((prev) => ({
        ...prev,
        data: equipmentItems,
      }));
    } catch (err) {
      toast.error(strings.errors.fetchEquipments);
    } finally {
      setEquipments((prev) => ({ ...prev, loading: false }));
    }
  };

  const getMaintenanceRoutineData = async (ids, eqRoutines) => {
    try {
      setMaintenanceRoutines({ data: [], loading: true });

      if (!online) {
        const localData = await localGetServiceOrder(serviceOrder.id);
        const routinesFound = localData?.routines ?? [];
        setMaintenanceRoutines((prev) => ({
          ...prev,
          data: [
            ...(eqRoutines?.map((eq) => {
              const routineFound = routinesFound.find(
                (m) => m.id === eq.routineID
              );
              return {
                ...routineFound,
                equipmentID: eq.equipmentID,
              };
            }) ?? []),
            ...(routinesFound.filter((m) => ids?.some((i) => i === m.id)) ??
              []),
          ],
        }));
        return;
      }

      const idsUnique =
        [
          ...new Set([
            ...(ids ?? []),
            ...(eqRoutines?.map((eq) => eq.routineID) ?? []),
          ]),
        ]?.filter((id) => !!id) ?? [];

      let routinesList = [];

      if (idsUnique?.length > 0) {
        await Promise.all(
          idsUnique.map(async (id) => {
            const routinesData = await API.graphql(
              graphqlOperation(getMaintenanceRoutine, {
                id,
              })
            );
            const routineItem = routinesData.data.getMaintenanceRoutine;

            routinesList.push(routineItem);
          }) ?? []
        );
      }

      setMaintenanceRoutines((prev) => ({
        ...prev,
        data: [
          ...(eqRoutines?.map((eq) => {
            const routineFound = routinesList.find(
              (m) => m.id === eq.routineID
            );
            return {
              ...routineFound,
              equipmentID: eq.equipmentID,
            };
          }) ?? []),
          ...(routinesList.filter((m) => ids?.some((i) => i === m.id)) ?? []),
        ],
      }));
    } catch (err) {
      toast.error(strings.errors.fetchMaintenanceRoutines);
    } finally {
      setMaintenanceRoutines((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    getMaintenanceRoutineData(maintenanceRoutineIDs, equipmentRoutines);
    if (equipmentRoutines?.length > 0)
      getEquipments(equipmentRoutines.map((eq) => eq.equipmentID));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maintenanceRoutineIDs, equipmentRoutines]);

  return (
    <Grid gap="5px">
      <span className="fs-5">{strings.maintenanceRoutines}</span>
      {maintenanceRoutines.loading ? (
        <Loader />
      ) : (
        <Grid gap="20px">
          {maintenanceRoutines.data.length > 0
            ? maintenanceRoutines.data.map((maintenanceRoutine) => {
                const equipment = maintenanceRoutine.equipmentID ? (
                  equipments.loading ? (
                    <Loader />
                  ) : (
                    equipments.data?.find(
                      (eq) => eq.id === maintenanceRoutine.equipmentID
                    )?.name ?? ""
                  )
                ) : null;
                return (
                  <CCard
                    borderRadius="medium"
                    variation="outlined"
                    style={{ height: "100%" }}
                  >
                    <Grid gap="5px" templateColumns={{ large: "5fr 0fr 19fr" }}>
                      <Grid
                        className="p-3"
                        gap="25px"
                        templateColumns={{ base: "1fr" }}
                        style={{ alignSelf: "start" }}
                      >
                        {maintenanceRoutine.equipmentID && (
                          <Grid>
                            <Label>{strings.equipment}</Label>
                            <span className="text-muted">{equipment}</span>
                          </Grid>
                        )}
                        <Grid>
                          <Label>{strings.name}</Label>
                          <span className="text-muted">
                            {maintenanceRoutine.name}
                          </span>
                        </Grid>
                        <Grid>
                          <Label>{strings.description}</Label>
                          <span className="text-muted">
                            {maintenanceRoutine.description}
                          </span>
                        </Grid>
                      </Grid>
                      <Divider size="small" orientation="vertical" />
                      <Grid
                        className="p-3"
                        gap="25px"
                        templateColumns={{ base: "1fr" }}
                        style={{ alignSelf: "start" }}
                      >
                        <ActivitiesInfo
                          maintenanceRoutineID={maintenanceRoutine.id}
                          equipmentID={maintenanceRoutine.equipmentID}
                          onChangeActivity={
                            onChangeActivity &&
                            ((actId, actVal) =>
                              onChangeActivity(
                                maintenanceRoutine.id,
                                actId,
                                actVal,
                                maintenanceRoutine.equipmentID
                              ))
                          }
                        />
                      </Grid>
                    </Grid>
                  </CCard>
                );
              })
            : strings.noMaintenanceRoutines}
        </Grid>
      )}
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    maintenanceRoutines: "Rutinas de Mantenimiento",
    equipment: "Equipo",
    name: "Nombre",
    description: "Descripción",
    createdAt: "Fecha de creación",
    errors: {
      fetchMaintenanceRoutines: "Error al consultar las rutinas",
      fetchEquipments: "Error al consultar los equipos",
    },
    noMaintenanceRoutines: "Sin rutinas...",
  },
});

export default MaintenanceRoutinesInfo;
